import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Config from '../utils/Config';
import { fetchFromAPI, formatPhoneNumber } from '../utils/functions';
import useTitle from '../hooks/useTitle';
import SearchBox from "./SearchBox";
import Pager from "./Pager";
import './SavedDealers.css';

const PAGE_SIZES = [10, 25, 50];

export default function SavedDealers({ isAdmin }) {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');
    const [messageClass, setMessageClass] = useState("hidden");
    const messageRef = useRef(null);

    useTitle('Saved Dealers');

    useEffect(() => {
        (async () => {
            const res = await fetchFromAPI(`${Config.ENDPOINTS.SAVED_DEALERS.BASE}/full`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            setRows(res?.data?.data || []);
        })();
    }, []); // eslint-disable-line

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter(r => {
            const d = r.dealer || {};
            const name = (d.BUSINESS_NAME || d.LICENSE_NAME || r.fflNumber || '').toLowerCase();
            const city = (d?.PREMISE_ADDRESS?.CITY || '').toLowerCase();
            const state = (d?.PREMISE_ADDRESS?.STATE || '').toLowerCase();
            return name.includes(needle) || city.includes(needle) || state.includes(needle);
        });
    }, [rows, q]);

    // reset page on search/page size change
    useEffect(() => { setPage(1); }, [q, pageSize]);

    // page math
    const filteredCount = filtered.length;
    const pageCount = Math.max(1, Math.ceil(filteredCount / pageSize));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * pageSize;
    const pageRows = filtered.slice(start, start + pageSize);

    async function unsave(id) {
        const prev = rows;
        setRows(prev.filter(r => r.dealerId !== id)); // optimistic
        try {
            await fetchFromAPI(Config.ENDPOINTS.SAVED_DEALERS.UNSAVE(id), {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch {
            setRows(prev); // revert
        }
    }

    const handleWebsiteClick = (verified, subdomain) => {
        if (verified) {
            const dealerURL = Config.API.WEBSITE_BASE_URL.replace("<sitename>", subdomain);
            window.open(dealerURL, "_blank", "noopener,noreferrer");
        }
    };

    const total = rows.length;
    const hasQuery = q.trim().length > 0;

    return (
        <div className="login-container login-container-dashboard">
            <div className="login-box" id="account-details-box">
                <header className="claims-h claims-header fflClaims-search-box">
                    <h2 className="dashboard-title">
                        <span className="titleIcon"><FontAwesomeIcon icon="bookmark" /></span>&nbsp;&nbsp;Saved Dealers
                    </h2>

                    <SearchBox
                        filteredClaims={filtered}          // pass filtered list for counts
                        onChange={setQ}
                        placeholder="Search dealers..."
                        ariaLabel="Search dealers"
                        shown={filteredCount}               // show filtered total, not just page slice
                        total={total}
                        hasQuery={hasQuery}
                        query={q}
                        setQuery={setQ}
                        pageSize={pageSize}
                        setPage={setPage}
                        setPageSize={setPageSize}
                    />
                </header>

                {/* Rows-per-page control (right-aligned, reuses Users styles) */}
                <div className="admin-table-controls users-tools mobile-search-box">
                    <div className="admin-table-right">
                        <label className="rows-label">Rows:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            className="rows-select"
                        >
                            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                </div>

                <div ref={messageRef} className={messageClass} />

                {filteredCount === 0 ? (
                    <div className="sdv-empty">Nothing saved yet...</div>
                ) : (
                    <ul className={`sdv-list ${pageRows.length === 1 ? 'sdv-list--single' : 'sdv-list--multiple'}`}>
                        {pageRows.map((r) => {
                            const d = r.dealer || {};
                            const addr = d.PREMISE_ADDRESS || {};
                            const name = d.BUSINESS_NAME || d.LICENSE_NAME || r.fflNumber || 'Dealer';
                            const address = [addr.STREET].filter(Boolean).join(', ');
                            const city = [addr.CITY, addr.STATE, addr.ZIP_CODE].filter(Boolean).join(', ');

                            return (
                                <li key={r.dealerId} className="sdv-row">
                                    <span className="sdv-rail" aria-hidden="true"></span>

                                    <button className="sdv-main" onClick={() => navigate(`/dealer/${r.dealerId}`)}>
                                        <div className="sdv-name">
                                            {name}
                                            {d.VERIFIED && <span className="sdv-badge">Verified</span>}
                                        </div>
                                        {address && <div className="sdv-sub line-2">{address}</div>}
                                        {city && <div className="sdv-sub line-2">{city}</div>}
                                        {d.VOICE_PHONE && <div className="sdv-sub">{formatPhoneNumber(d.VOICE_PHONE)}</div>}
                                    </button>

                                    <div className="sdv-actions">
                                        {d.VERIFIED && (
                                            <a
                                                className="sdv-btn website"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Visit website"
                                                aria-label="Visit website"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleWebsiteClick(d.VERIFIED, d.SUBDOMAIN);
                                                }}
                                            >
                                                🌐
                                            </a>
                                        )}
                                        <button
                                            className="sdv-btn open"
                                            onClick={() => navigate(`/dealer/${r.dealerId}`)}
                                            title="Open"
                                            aria-label="Open"
                                        >
                                            ➜
                                        </button>
                                        <button
                                            className="sdv-btn danger"
                                            onClick={() => unsave(r.dealerId)}
                                            title="Remove"
                                            aria-label="Remove"
                                        >
                                            🗑
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {/* Pager */}
                {filteredCount > 0 && (
                    <Pager
                        safePage={safePage}
                        setPage={setPage}
                        pageCount={pageCount}
                    />
                )}
            </div>
        </div>
    );
}